import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll event to add background color when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when window resizes to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <>
      <header className={`header ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
        <div className="container">
          <div className="nav-container">
            <Link to="/" className="logo" onClick={() => setMenuOpen(false)}>
              <img src="/favicon.ico" alt="Voters Deserve More logo" className="logo-img" />
              <span>Voters Deserve More</span>
            </Link>

            <button 
              className={`mobile-menu-toggle ${menuOpen ? 'active' : ''}`} 
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <nav className={menuOpen ? 'active' : ''}>
              <ul>
                <li>
                  <Link to="/about" onClick={() => setMenuOpen(false)}>
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/demands" onClick={() => setMenuOpen(false)}>
                    Our Reforms
                  </Link>
                </li>
                <li>
                  <Link to="/voting-systems" onClick={() => setMenuOpen(false)}>
                    Voting Systems
                  </Link>
                </li>
                <li>
                  <Link to="/simulator" onClick={() => setMenuOpen(false)}>
                    Voting Simulator
                  </Link>
                </li>
                <li>
                  <Link to="/rank-the-blank" onClick={() => setMenuOpen(false)}>
                    Rank The Blank
                  </Link>
                </li>
                <li>
                  <Link to="/get-involved" className="cta-button" onClick={() => setMenuOpen(false)}>
                    Get Involved
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </header>

      <style jsx>{`
        /* Header styling */
        .header {
          background-color: var(--primary, #2D336B);
          padding: 1rem 0;
          position: sticky;
          top: 0;
          z-index: 100;
          transition: background-color 0.3s ease, box-shadow 0.3s ease;
        }

        .header.scrolled {
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
          background-color: var(--primary, #2D336B);
        }

        .container {
          width: 90%;
          max-width: 1200px;
          margin: 0 auto;
        }

        .nav-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--light, #FFF2F2);
          text-decoration: none;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .logo-img {
          height: 2rem;
          width: auto;
        }

        nav ul {
          display: flex;
          list-style-type: none;
          gap: 2rem;
          margin: 0;
          padding: 0;
        }

        nav ul li a {
          color: var(--light, #FFF2F2);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s;
        }

        nav ul li a:hover {
          color: var(--tertiary, #A9B5DF);
        }

        .cta-button {
          background-color: var(--accent, #ff6b6b);
          color: white;
          padding: 0.75rem 1.5rem;
          border-radius: 5px;
          font-weight: 600;
          text-decoration: none;
          transition: background-color 0.3s, transform 0.2s;
        }

        .cta-button:hover {
          background-color: #e55c5c;
          transform: translateY(-2px);
        }

        /* Mobile menu toggle button */
        .mobile-menu-toggle {
          display: none;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 10px;
          z-index: 101;
        }

        .mobile-menu-toggle span {
          display: block;
          width: 28px;
          height: 3px;
          background-color: var(--light, #FFF2F2);
          margin: 5px 0;
          transition: transform 0.3s, opacity 0.3s;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .mobile-menu-toggle {
            display: block;
          }
          
          .nav-container {
            position: relative;
          }
          
          nav {
            position: fixed;
            top: 0;
            right: -100%;
            width: 80%;
            max-width: 300px;
            height: 100vh;
            background-color: var(--primary, #2D336B);
            padding: 80px 2rem 2rem;
            transition: right 0.3s ease-in-out;
            box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
            z-index: 100;
          }
          
          nav.active {
            right: 0;
          }
          
          nav ul {
            flex-direction: column;
            gap: 1.5rem;
          }
          
          nav ul li {
            width: 100%;
          }
          
          nav ul li a {
            display: block;
            padding: 0.5rem 0;
            font-size: 1.1rem;
          }
          
          /* Animation for hamburger menu */
          .mobile-menu-toggle.active span:nth-child(1) {
            transform: translateY(8px) rotate(45deg);
          }
          
          .mobile-menu-toggle.active span:nth-child(2) {
            opacity: 0;
          }
          
          .mobile-menu-toggle.active span:nth-child(3) {
            transform: translateY(-8px) rotate(-45deg);
          }
          
          /* Add an overlay when mobile menu is open */
          .header.menu-open::after {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 99;
          }
          
          /* Adjust logo size on mobile */
          .logo {
            position: relative;
            z-index: 98;
          }
          
          .logo span {
            font-size: 1.1rem;
          }
          
          .logo-img {
            height: 1.8rem;
          }
          
          /* Fix for logo text appearing above the sidebar menu */
          .header.menu-open .logo {
            visibility: visible;
            opacity: 1;
            transition: opacity 0.3s;
          }
          
          .header.menu-open .logo span {
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.3s, visibility 0.3s;
          }
        }
      `}</style>
    </>
  );
};

export default Header;